// Zod schemas for every content pack. Content is validated at boot — content errors
// must surface as readable messages, never silent runtime explosions.

import { z } from 'zod';
import type {
  AssetDef,
  BusinessDef,
  EventCardDef,
  LiabilityDef,
  QuestDef,
  SkillDef,
  TuningDef,
  BuilderContent,
} from './types.js';

export const SkillIdSchema = z.enum(['negotiation', 'marketing', 'quality', 'finance', 'leadership']);

export const TuningSchema: z.ZodType<TuningDef> = z.object({
  startingCash: z.number().nonnegative(),
  livingExpensesPerWeek: z.number().nonnegative(),
  apPerDay: z.number().int().positive(),
  inGameDaysPerWeek: z.number().int().positive(),
  savingsRatePerWeek: z.number().min(0).max(1),
  loan: z.object({
    ratePerWeek: z.number().min(0).max(1),
    defaultTermWeeks: z.number().int().positive(),
    maxPrincipalAtStart: z.number().positive(),
  }),
  reputation: z.object({
    start: z.number().min(0).max(100),
    min: z.number().min(0).max(100),
    max: z.number().min(0).max(100),
  }),
  creditScore: z.object({
    start: z.number().min(300).max(850),
    min: z.number().min(300).max(850),
    max: z.number().min(300).max(850),
  }),
  catchupMaxDays: z.number().int().positive(),
});

export const BusinessSchema: z.ZodType<BusinessDef> = z.object({
  id: z.string(),
  name: z.string(),
  equipmentCost: z.number().nonnegative(),
  fixedPerWeek: z.number().nonnegative(),
  cogsPerUnit: z.number().nonnegative(),
  sweetPrice: z.number().positive(),
  baseDemand: z.number().positive(),
  capacity: z.number().positive(),
  qualityFactorDefault: z.number().min(0.5).max(1.5),
  unlock: z.object({ level: z.number().int().optional() }).optional(),
});

export const AssetSchema: z.ZodType<AssetDef> = z.object({
  id: z.string(),
  name: z.string(),
  cost: z.number().nonnegative(),
  weeklyYield: z.number().nonnegative(),
  category: z.string().optional(),
});

export const LiabilitySchema: z.ZodType<LiabilityDef> = z.object({
  id: z.string(),
  name: z.string(),
  cost: z.number().nonnegative(),
  weeklyUpkeep: z.number().nonnegative(),
  happinessGain: z.number(),
});

const EffectSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('demandMultiplier'),
    businessDefId: z.string().optional(),
    multiplier: z.number().positive(),
  }),
  z.object({
    kind: z.literal('cogsDelta'),
    businessDefId: z.string().optional(),
    delta: z.number(),
  }),
  z.object({ kind: z.literal('reputationDelta'), delta: z.number() }),
  z.object({ kind: z.literal('oneTimeCash'), amount: z.number(), note: z.string() }),
]);

export const EventCardSchema: z.ZodType<EventCardDef> = z.object({
  id: z.string(),
  title: z.string(),
  weight: z.number().nonnegative(),
  durationWeeks: z.number().int().positive(),
  effects: z.array(EffectSchema),
});

export const SkillSchema: z.ZodType<SkillDef> = z.object({
  id: SkillIdSchema,
  name: z.string(),
  description: z.string(),
  maxLevel: z.number().int().positive(),
  xpPerLevel: z.array(z.number().nonnegative()),
});

export const QuestSchema: z.ZodType<QuestDef> = z.object({
  id: z.string(),
  name: z.string(),
  target: z.number().positive(),
  metric: z.enum(['cashEarned', 'passiveIncome', 'businessesStarted']),
});

export const BuilderContentSchema: z.ZodType<BuilderContent> = z.object({
  mode: z.literal('builder'),
  enabled: z.literal(true),
  tuning: TuningSchema,
  businesses: z.array(BusinessSchema).min(1),
  assets: z.array(AssetSchema),
  liabilities: z.array(LiabilitySchema),
  events: z.array(EventCardSchema),
  skills: z.array(SkillSchema),
  quests: z.array(QuestSchema),
});

export const StubContentSchema = z.object({
  mode: z.enum(['explorer', 'founder']),
  enabled: z.literal(false),
  comingSoon: z.literal(true),
});
